import React, { useEffect, useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from 'moment';
import { useSelector } from 'react-redux';
import TableShimmer from '../common/components/TableShimmer';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import useReportStyles from './common/useReportStyles';
import ReportFilter from './components/ReportFilter';
import ReportsMenu from './components/ReportsMenu';
import CorteCajaPrintView from './components/CorteCajaPrintView';
import { useCatch, useEffectAsync } from '../reactHelper';

// Obtener hora de salida
const formatSalidaHora = (salida) => {
    const rawDate = salida?.date || (salida?.tickets && salida.tickets[0] && (salida.tickets[0].expectedTime || salida.tickets[0].enterTime));
    if (!rawDate) return '-';
    return moment(rawDate).format('HH:mm');
};

//Obtener nombre de la unidad desde salida. En caso de que no venga, buscar dispositivo en estado global por deviceId
const getDeviceName = (salida, devicesMap) => {
    if (!salida) return '-';
    if (salida.device) {
        if (typeof salida.device === 'object') {
            return (
                salida.device.name ||
                devicesMap[salida.deviceId]?.name ||
                String(salida.deviceId) ||
                '-'
            );
        }
        return '-'
    }
    if (salida.deviceId) {
        return devicesMap[salida.deviceId]?.name || String(salida.deviceId);
    }
    return '-';
};

// Obtener nombre del itinerario desde la respuesta
const getItinerarioName = (salida) => {
    if (!salida) return '-';

    if (salida.itinerario) {
        return salida.itinerario.name || '-';
    }

    return '-';
};

// Obtener nombre de la unidad que le sigue a la actual
const getFollowingDeviceName = (salida, devices) => {
    if (!salida) return '-';
    if (salida.followingDevice) {
        if (typeof salida.followingDevice === 'object') {
            return (
                salida.followingDevice.name ||
                devices[salida.followingDevice.id]?.name ||
                String(salida.followingDevice.id) ||
                '-'
            );
        }
        return '-';
    }
    if (salida.followingDeviceId) {
        return devices[salida.followingDeviceId]?.name || String(salida.followingDeviceId);
    }
    return '-';
};

const processReceiptsToRows = (receiptsList, devicesMap) => {
    const rows = [];

    (receiptsList || []).forEach((receipt) => {
        (receipt.salidas || []).forEach((salida) => {
            const deviceName = getDeviceName(salida, devicesMap);
            const itinerario = getItinerarioName(salida);
            const horaSalida = formatSalidaHora(salida);
            const leSigue = getFollowingDeviceName(salida, devicesMap);
            //Si salida tiene algo en el campo excuse entonces se omite la salida
            const salidaOmitted = Boolean(salida.excuse && salida.excuse.trim() !== '');

            let minutos = 0;
            let excusas = 0;
            let importe = 0;

            (salida.tickets || []).forEach((ticket) => {
                const hasEnter = Boolean(ticket.enterTime);
                const diff = Math.max(0, ticket.difference || 0);
                const isExcused =
                    salidaOmitted ||
                    Boolean(ticket.excuse && ticket.excuse.trim() !== '') ||
                    Boolean(ticket.globalExcuse);

                const punishment = Math.max(0, Number(ticket.punishment) || 0);

                if (hasEnter) {
                    if (diff > 0) {
                        minutos += diff;
                        if (isExcused) {
                            excusas += diff;
                        }
                    }

                    if (!isExcused) {
                        importe += punishment;
                    }
                }
            });

            rows.push({
                id: salida.id,
                receiptId: receipt.id,
                unidad: deviceName,
                itinerario,
                horaSalida,
                minutos,
                importe,
                excusas,
                leSigue,
                salidaOmitted,
                tickets: (salida.tickets || []).map((t) => ({
                    ...t,
                    punishment: Math.max(0, Number(t.punishment) || 0),
                })),
            });
        });
    });

    return rows;
};

const printCorteCaja = ({ rows, date, checador, total, totalMinutos, totalExcusas }) => {
    const win = window.open('', '_blank');
    if (!win) {
        alert('El navegador bloqueó la ventana de impresión. Por favor permita ventanas emergentes.');
        return;
    }

    win.document.write(
        renderToStaticMarkup(
            <CorteCajaPrintView data={{ rows, date, checador, total, totalMinutos, totalExcusas }} />
        )
    );
    win.document.close();
    win.focus();
    win.print();
    win.close();
};

const TicketDetailModal = ({ open, onClose, salida }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!salida) return null;

    const tickets = salida.tickets || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    m: isMobile ? 1.5 : 3,
                    maxHeight: isMobile ? '92vh' : '90vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: isMobile ? 2 : 3,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f0f0f0',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} color="text.primary">
                        Salida #{salida.id}
                    </Typography>
                    {salida.salidaOmitted && (
                        <Chip label="Omitida" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 1.5 : 3 }}>
                {/* Meta & Stats summary */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        gap: 1,
                        mb: 2,
                        p: 1.5,
                        backgroundColor: '#f8fafc',
                        borderRadius: 1.5,
                        border: '1px solid #e2e8f0',
                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, color: 'text.secondary' }}>
                        <span><strong>UNIDAD:</strong> {salida.unidad}</span>
                        <span><strong>ITINERARIO:</strong> {salida.itinerario}</span>
                        <span><strong>HORA SALIDA:</strong> {salida.horaSalida}</span>
                        {salida.leSigue && salida.leSigue !== '-' && (
                            <span><strong>LE SIGUE:</strong> {salida.leSigue}</span>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, pt: isMobile ? 0.5 : 0, borderTop: isMobile ? '1px solid #e2e8f0' : 'none', width: isMobile ? '100%' : 'auto' }}>
                        <span>TICKETS: <strong>{tickets.length}</strong></span>
                        <span>MINUTOS: <strong style={{ color: salida.minutos > 0 ? '#d32f2f' : 'inherit' }}>{salida.minutos}</strong></span>
                        <span>TOTAL: <strong style={{ color: salida.importe > 0 ? '#d32f2f' : '#2e7d32' }}>${salida.importe}</strong></span>
                    </Box>
                </Box>

                {/* Responsive Tickets View: Cards on Mobile, Table on Desktop */}
                {isMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {tickets.length === 0 ? (
                            <Typography variant="body2" sx={{ py: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.8rem' }}>
                                No hay tickets registrados.
                            </Typography>
                        ) : (
                            tickets.map((ticket, index) => {
                                const hasEnter = Boolean(ticket.enterTime);
                                const diff = ticket.difference || 0;
                                const isExcused =
                                    salida.salidaOmitted ||
                                    Boolean(ticket.excuse && ticket.excuse.trim() !== '') ||
                                    Boolean(ticket.globalExcuse);
                                const punishment = Math.max(0, Number(ticket.punishment) || 0);
                                const isApplied = punishment > 0 && !isExcused && hasEnter;

                                return (
                                    <Paper
                                        key={ticket.id || index}
                                        variant="outlined"
                                        sx={{
                                            p: 1.2,
                                            borderRadius: 2,
                                            backgroundColor: isApplied ? '#fff5f5' : '#ffffff',
                                            border: isApplied ? '1px solid #ffcdd2' : '1px solid #e2e8f0',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'primary.main' }}>
                                                TICKET #{ticket.id || index + 1}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    color: isApplied ? 'error.main' : 'text.secondary',
                                                    textDecoration: (isExcused || !hasEnter) && punishment > 0 ? 'line-through' : 'none',
                                                }}
                                            >
                                                CASTIGO: ${punishment}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', fontSize: '0.7rem' }}>
                                                    PROGRAMADA:
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                    {ticket.expectedTime ? moment(ticket.expectedTime).format('HH:mm:ss') : '—'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', fontSize: '0.7rem' }}>
                                                    REAL:
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: hasEnter ? 'text.primary' : '#9e9e9e' }}>
                                                    {ticket.enterTime ? moment(ticket.enterTime).format('HH:mm:ss') : 'Sin entrada'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', fontSize: '0.7rem' }}>
                                                    DIFERENCIA:
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: diff > 0 && hasEnter ? 'error.main' : 'text.secondary' }}>
                                                    {hasEnter ? (diff > 0 ? `+${diff} min` : `${diff} min`) : '—'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', fontSize: '0.7rem' }}>
                                                    EXCUSA:
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: isExcused ? '#ed6c02' : 'text.secondary', fontWeight: isExcused ? 500 : 400 }}>
                                                    {!hasEnter ? 'Sin entrada' : isExcused ? (ticket.excuse || (ticket.globalExcuse ? 'Excusa Global' : 'Excusado')) : '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                );
                            })
                        )}
                    </Box>
                ) : (
                    /* Desktop Tickets Table */
                    <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                                        TICKET
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                                        PROGRAMADA
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                                        REAL
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                                        DIFERENCIA
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                                        CASTIGO
                                    </TableCell>
                                    <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 1 }}>
                                        EXCUSA
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tickets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                            No hay tickets registrados.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    tickets.map((ticket, index) => {
                                        const hasEnter = Boolean(ticket.enterTime);
                                        const diff = ticket.difference || 0;
                                        const isExcused =
                                            salida.salidaOmitted ||
                                            Boolean(ticket.excuse && ticket.excuse.trim() !== '') ||
                                            Boolean(ticket.globalExcuse);

                                        const punishment = Math.max(0, Number(ticket.punishment) || 0);
                                        const isApplied = punishment > 0 && !isExcused && hasEnter;

                                        return (
                                            <TableRow
                                                key={ticket.id || index}
                                                sx={{
                                                    '&:hover': { backgroundColor: '#f9fafb' },
                                                    backgroundColor: isApplied ? '#fff5f5' : 'inherit',
                                                }}
                                            >
                                                <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                                    #{ticket.id}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {ticket.expectedTime ? moment(ticket.expectedTime).format('HH:mm:ss') : '—'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {ticket.enterTime ? moment(ticket.enterTime).format('HH:mm:ss') : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: diff > 0 ? 600 : 400,
                                                            color: diff > 0 ? 'error.main' : 'text.secondary',
                                                        }}
                                                    >
                                                        {diff > 0 ? `+${diff} min` : `${diff} min`}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: isApplied ? 600 : 400,
                                                            color: isApplied ? 'error.main' : 'text.secondary',
                                                            textDecoration: (isExcused || !hasEnter) && punishment > 0 ? 'line-through' : 'none',
                                                        }}
                                                    >
                                                        ${punishment}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="left">
                                                    {!hasEnter ? (
                                                        <Typography variant="caption" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                                                            Sin entrada
                                                        </Typography>
                                                    ) : isExcused ? (
                                                        <Typography variant="caption" sx={{ color: '#ed6c02', fontWeight: 500 }}>
                                                            {ticket.excuse || (ticket.globalExcuse ? 'Excusa Global' : 'Excusado')}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                                            —
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ px: isMobile ? 2 : 3, py: 1.5, borderTop: '1px solid #f0f0f0' }}>
                <Button onClick={onClose} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: 1.5 }}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const RowItem = ({ row, onOpenDetail }) => {
    const hasTickets = row.tickets && row.tickets.length > 0;

    return (
        <TableRow
            sx={{
                '&:nth-of-type(2n)': { backgroundColor: '#fcfcfc' },
                '&:hover': { backgroundColor: '#f0f4f8' },
                backgroundColor: row.salidaOmitted ? '#fffde7' : 'inherit',
            }}
        >
            <TableCell align="center">
                <Typography fontWeight="bold" variant="body2">
                    {row.unidad}
                </Typography>
            </TableCell>
            <TableCell align="left">
                <Typography variant="body2">
                    {row.itinerario}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Typography variant="body2">
                    {row.horaSalida}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography variant="body2" color={row.minutos > 0 ? 'error.main' : 'text.primary'}>
                    {row.minutos}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography fontWeight="bold" variant="body2" color={row.importe > 0 ? 'error.main' : 'success.main'}>
                    {row.importe}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography variant="body2" color={row.excusas > 0 ? 'warning.main' : 'text.secondary'}>
                    {row.excusas}
                </Typography>
            </TableCell>
            <TableCell align="center">
                <Typography fontWeight="medium" variant="body2">
                    {row.leSigue}
                </Typography>
            </TableCell>
            <TableCell align="center" sx={{ width: 50, p: 0.5 }}>
                {hasTickets && (
                    <Tooltip title="Ver detalle de tickets">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onOpenDetail(row)}
                            sx={{
                                backgroundColor: '#e3f2fd',
                                '&:hover': { backgroundColor: '#bbdefb' },
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
};

const CortesCajaReportPage = () => {
    const classes = useReportStyles();
    const t = useTranslation();

    const devices = useSelector((state) => state.devices.items);
    const reduxFrom = useSelector((state) => state.reports.from);

    const [user, setUser] = useState('');
    const [subusers, setSubusers] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDateStr, setSelectedDateStr] = useState(moment().format('DD/MM/YYYY'));
    const [selectedSalidaForModal, setSelectedSalidaForModal] = useState(null);

    useEffect(() => {
        if (reduxFrom) {
            setSelectedDateStr(moment(reduxFrom).format('DD/MM/YYYY'));
        }
    }, [reduxFrom]);

    useEffectAsync(async () => {
        try {
            const usersResponse = await fetch('/api/users');
            if (usersResponse.ok) {
                const _subusers = await usersResponse.json();
                setSubusers(_subusers);
            }
        } catch (e) {
            console.error('Error fetching users', e);
        }

    }, []);

    const fetchReceipts = async (query) => {
        try {
            setLoading(true);
            const response = await fetch(`/api/reports/recibos?${query.toString()}`, {
                headers: { Accept: 'application/json' },
            });

            if (!response.ok) {
                throw Error(await response.text());
            }

            const items = await response.json();
            setReceipts(items);
            return items;
        } finally {
            setLoading(false);
        }
    };

    const getChecadorName = (userId, items) => {
        if (userId) {
            const found = subusers.find((u) => String(u.id) === String(userId));
            if (found) return found.name;
        }
        if (items && items.length > 0 && items[0].userId) {
            const found = subusers.find((u) => u.id === items[0].userId);
            if (found) return found.name;
        }
        return '';
    };

    const handleSubmit = useCatch(async ({ deviceIds, groupIds, from: selectedFrom, to: selectedTo, type }) => {
        const query = new URLSearchParams({ from: selectedFrom, to: selectedTo });
        deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
        groupIds.forEach((groupId) => query.append('groupId', groupId));
        if (user) {
            query.append('user', parseInt(user, 10));
        }

        const dateFormatted = selectedFrom ? moment(selectedFrom).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY');
        setSelectedDateStr(dateFormatted);

        const items = await fetchReceipts(query);

        if (type === 'export') {
            const rows = processReceiptsToRows(items, devices);
            const total = rows.reduce((sum, row) => sum + row.importe, 0);
            const totalMin = rows.reduce((sum, row) => sum + row.minutos, 0);
            const totalExc = rows.reduce((sum, row) => sum + row.excusas, 0);
            const checadorName = getChecadorName(user, items);
            printCorteCaja({
                rows,
                date: dateFormatted,
                checador: checadorName,
                total,
                totalMinutos: totalMin,
                totalExcusas: totalExc,
            });
        }
    });

    const selectedChecadorName = useMemo(() => {
        return getChecadorName(user, receipts);
    }, [user, subusers, receipts]);

    const reportRows = useMemo(() => {
        return processReceiptsToRows(receipts, devices);
    }, [receipts, devices]);

    const totalImporte = useMemo(() => {
        return reportRows.reduce((sum, row) => sum + row.importe, 0);
    }, [reportRows]);

    const totalMinutos = useMemo(() => {
        return reportRows.reduce((sum, row) => sum + row.minutos, 0);
    }, [reportRows]);

    const totalExcusas = useMemo(() => {
        return reportRows.reduce((sum, row) => sum + row.excusas, 0);
    }, [reportRows]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportRoute']}>
            <div className={classes.container}>
                <div className={classes.containerMain}>
                    <div className={classes.header}>
                        <ReportFilter
                            handleSubmit={handleSubmit}
                            handleSchedule={() => { }}
                            periodOptions={['singleDate', 'today', 'yesterday', 'thisWeek', 'custom']}
                            ignoreDevice
                        >
                            <FormControl style={{ minWidth: '180px' }}>
                                <InputLabel>{t('settingsUsers')}</InputLabel>
                                <Select
                                    label={t('settingsUsers')}
                                    value={user}
                                    onChange={(event) => setUser(event.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>-- Todos los checadores --</em>
                                    </MenuItem>

                                    {subusers.map((s) => (
                                        <MenuItem key={`user-${s.id}`} value={s.id}>
                                            {s.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </ReportFilter>
                    </div>

                    {(loading || reportRows.length > 0) && (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Summary Bar */}
                            <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        Corte de Caja
                                    </Typography>
                                    <Chip label={`Fecha: ${selectedDateStr}`} variant="outlined" size="small" />
                                    {selectedChecadorName && (
                                        <Chip label={`Checador: ${selectedChecadorName}`} color="primary" variant="outlined" size="small" />
                                    )}
                                    <Chip label={`Salidas: ${reportRows.length}`} color="default" size="small" />
                                </Box>
                                {isMobile && (
                                    <Box sx={{ width: '100%', pt: 1, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                                            TOTAL DEL DIA:
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold" sx={{ color: totalImporte > 0 ? 'error.main' : 'success.main' }}>
                                            ${totalImporte}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>

                            {/* Responsive View: Cards on Mobile, Table on Desktop */}
                            {isMobile ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {loading ? (
                                        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                                            <Table size="small">
                                                <TableBody>
                                                    <TableShimmer columns={3} />
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        reportRows.map((row) => (
                                            <Card
                                                key={`card-salida-${row.id}`}
                                                elevation={1}
                                                sx={{
                                                    borderRadius: 2,
                                                    border: '1px solid #e0e0e0',
                                                    '&:hover': { borderColor: '#90caf9' },
                                                }}
                                            >
                                                <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem' }}>
                                                                UNIDAD:
                                                            </Typography>
                                                            <Chip label={row.unidad} size="small" color="primary" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.75rem' }} />
                                                            {row.salidaOmitted && (
                                                                <Chip label="Omitida" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem' }}>
                                                                HORA SALIDA:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ fontSize: '0.75rem' }}>
                                                                {row.horaSalida}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box sx={{ mb: 0.5 }}>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem', display: 'inline', mr: 0.5 }}>
                                                            ITINERARIO:
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.75rem' }}>
                                                            {row.itinerario}
                                                        </Typography>
                                                    </Box>

                                                    {row.leSigue && row.leSigue !== '-' && (
                                                        <Box sx={{ mb: 0.75 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem', display: 'inline', mr: 0.5 }}>
                                                                LE SIGUE:
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.75rem' }}>
                                                                {row.leSigue}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.75, borderTop: '1px solid #f1f5f9', mt: 0.5 }}>
                                                        <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                            <span>MIN: <strong style={{ color: row.minutos > 0 ? '#d32f2f' : 'inherit' }}>{row.minutos}</strong></span>
                                                            <span>EXC: <strong>{row.excusas}</strong></span>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.7rem' }}>
                                                                IMPORTE:
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight="bold" sx={{ color: row.importe > 0 ? 'error.main' : 'success.main', fontSize: '0.85rem' }}>
                                                                ${row.importe}
                                                            </Typography>
                                                            <Tooltip title="Ver tickets">
                                                                <IconButton size="small" onClick={() => setSelectedSalidaForModal(row)} color="primary" sx={{ p: 0.5 }}>
                                                                    <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))
                                    )}
                                </Box>
                            ) : (
                                /* Desktop Table */
                                <TableContainer component={Paper} sx={{ boxShadow: 2, overflowX: 'auto' }}>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    UNIDAD
                                                </TableCell>
                                                <TableCell align="left" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    ITINERARIO
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    HORA SALIDA
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    MINUTOS
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    IMPORTE
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    EXCUSAS
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    LE SIGUE
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.85rem', width: 50 }}>
                                                    TICKETS
                                                </TableCell>
                                            </TableRow>
                                            {!loading && reportRows.length > 0 && (
                                                <TableRow sx={{ backgroundColor: '#e8f4fd', borderBottom: '2px solid #90caf9' }}>
                                                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#0d47a1' }}>
                                                        TOTAL DEL DIA
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                                        {totalMinutos}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: totalImporte > 0 ? 'error.main' : 'success.main' }}>
                                                        ${totalImporte}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                                                        {totalExcusas}
                                                    </TableCell>
                                                    <TableCell colSpan={2} />
                                                </TableRow>
                                            )}
                                        </TableHead>

                                        <TableBody>
                                            {loading ? (
                                                <TableShimmer columns={8} />
                                            ) : (
                                                reportRows.map((row) => (
                                                    <RowItem
                                                        key={`salida-${row.id}`}
                                                        row={row}
                                                        onOpenDetail={(r) => setSelectedSalidaForModal(r)}
                                                    />
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {/* Ticket Detail Modal */}
                            <TicketDetailModal
                                open={Boolean(selectedSalidaForModal)}
                                salida={selectedSalidaForModal}
                                onClose={() => setSelectedSalidaForModal(null)}
                            />
                        </Box>
                    )}


                </div>
            </div>
        </PageLayout>
    );
};

export default CortesCajaReportPage;
